import { createContext, useContext } from "react";
import type { RepoBrief } from "../../shared/types.js";

/**
 * The active content repo's name — the `/<repo>/…` URL prefix. Provided at the
 * App root (derived from the URL) and read wherever a repo-scoped API call or
 * link needs to know which repo it's in.
 */
export const RepoContext = createContext<string>("");

export function useRepo(): string {
  return useContext(RepoContext);
}

/** Reserved first segments that are NOT a repo name (system routes). */
const SYSTEM_SEGMENTS = new Set([
  "_data",
  "_tree",
  "_repo",
  "_tools",
  "_status",
  "_cleanup",
  "_apps",
  "_tokens",
  "_local",
]);

/**
 * The active repo for a pathname. Content pages carry the repo as the first
 * segment (`/<repo>/…`); `/_repo/<repo>` carries it second; other system pages
 * have none, so we fall back to the default (first) repo.
 */
export function repoFromPath(pathname: string, fallback: string): string {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 0) return fallback;
  if (segs[0] === "_repo") return segs[1] ?? fallback;
  if (SYSTEM_SEGMENTS.has(segs[0]) || segs[0].startsWith("_")) return fallback;
  return segs[0];
}

/** Strip the leading `/<repo>` from a content pathname → the repo-relative path. */
export function repoRelativePath(pathname: string, repo: string): string {
  const clean = pathname.replace(/^\/+/, "");
  if (clean === repo) return "";
  return clean.startsWith(`${repo}/`) ? clean.slice(repo.length + 1) : clean;
}

/**
 * The repos on show for the active repo: a hidden one you're *inside* stands
 * alone, otherwise every non-hidden repo. Both the sidebar accordion and the
 * header search read this — searching what you can't see is the same surprise
 * as a sidebar that lists it. So inside `/dwdocs/…` search is dwdocs, and
 * everywhere else it's the visible repos.
 */
export function reposOnShow(repos: RepoBrief[], currentRepo: string): RepoBrief[] {
  const focused = repos.find((r) => r.name === currentRepo && r.hidden);
  return focused ? [focused] : repos.filter((r) => !r.hidden);
}

/** Build a content URL within a repo: `/<repo>/<path>` (path may be empty). */
export function repoHref(repo: string, path = ""): string {
  return path ? `/${repo}/${path}` : `/${repo}/`;
}
